// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { PrimaryButton, SecondaryButtonInline } from '../../components';
import type { BxAppDefinition, BxAppId } from '../../config/bxApps';
import { getAppLabel } from '../../config/bxApps';
import { resolveBxAppLaunchHref } from '../../config/railNavigation';
import { useActivePersonaMaybe } from '../../context/ActivePersonaContext';
import { useActivityCart } from '../../context/ActivityCartContext';
import { useWalletSession } from '../../context/WalletSessionContext';
import Web3WalletAuthPanel from '../../containers/AuthenticationPage/Web3WalletAuthPanel/Web3WalletAuthPanel';

import css from './GuidedAppFlow.module.css';

type GuidedAppFlowProps = {
  app: BxAppDefinition;
  draftId?: string | null;
  initialStep?: number;
  initialFormData?: Record<string, string>;
};

const gateSatisfied = (gate, { authMode, personaContext }) => {
  if (!gate) {
    return true;
  }
  if (gate === 'wallet') {
    return authMode === 'web3';
  }
  if (gate === 'persona_merchant') {
    return personaContext?.activatedPersonas?.includes('merchant');
  }
  if (gate === 'persona_runner') {
    return personaContext?.activatedPersonas?.includes('runner');
  }
  return true;
};

const CrossAppChip = ({ appId, label }: { appId: BxAppId; label: string }) => (
  <Link to={`/apps/${appId}`} className={css.chip}>
    {label}
  </Link>
);

const GuidedAppFlow = ({
  app,
  draftId: initialDraftId = null,
  initialStep = 0,
  initialFormData = {},
}: GuidedAppFlowProps) => {
  const { authMode } = useWalletSession();
  const personaContext = useActivePersonaMaybe();
  const { upsertDraft, updateDraft } = useActivityCart();

  const [stepIndex, setStepIndex] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);
  const [draftId, setDraftId] = useState(initialDraftId);

  const steps = app.steps;
  const step = steps[stepIndex];
  const isLastStep = stepIndex >= steps.length - 1;

  const canProceed = useMemo(() => {
    if (!step) {
      return false;
    }
    if (step.gate && !gateSatisfied(step.gate, { authMode, personaContext })) {
      return false;
    }
    if (step.fields) {
      return step.fields.every(f => !f.required || (formData[f.id]?.trim?.() ?? formData[f.id]));
    }
    return true;
  }, [step, formData, authMode, personaContext]);

  const persistDraft = useCallback(
    (nextStepIndex, status = 'draft') => {
      const title =
        formData.itemTitle ||
        formData.eventTitle ||
        formData.purchaseDesc?.slice(0, 48) ||
        app.name;
      const id = upsertDraft({
        id: draftId || undefined,
        appId: app.id,
        title,
        stepIndex: nextStepIndex,
        formData,
        status,
        resumePath: `${app.hubPath}${draftId ? `?draft=${draftId}` : ''}`,
        launchHref: app.launchHref,
      });
      if (!draftId) {
        setDraftId(id);
      } else {
        updateDraft(draftId, { stepIndex: nextStepIndex, formData, status });
      }
      return id;
    },
    [app, draftId, formData, upsertDraft, updateDraft]
  );

  const handleNext = () => {
    persistDraft(stepIndex + 1, isLastStep ? 'ready' : 'draft');
    if (!isLastStep) {
      setStepIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(prev => prev - 1);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  if (!step) {
    return null;
  }

  const showWalletPanel = step.gate === 'wallet' && authMode !== 'web3';
  const showPersonaHint =
    (step.gate === 'persona_merchant' || step.gate === 'persona_runner') &&
    !gateSatisfied(step.gate, { authMode, personaContext });

  return (
    <div className={css.root} data-testid={`guided-flow-${app.id}`}>
      <nav className={css.stepNav} aria-label="Progress">
        {steps.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            className={
              idx === stepIndex
                ? `${css.stepDot} ${css.stepDotActive}`
                : idx < stepIndex
                  ? `${css.stepDot} ${css.stepDotDone}`
                  : css.stepDot
            }
            onClick={() => idx <= stepIndex && setStepIndex(idx)}
            aria-current={idx === stepIndex ? 'step' : undefined}
            aria-label={`Step ${idx + 1}: ${s.title}`}
          >
            {idx + 1}
          </button>
        ))}
      </nav>

      <article className={css.stepCard}>
        <p className={css.stepEyebrow}>
          Step {stepIndex + 1} of {steps.length}
        </p>
        <h2 className={css.stepTitle}>{step.title}</h2>
        <p className={css.stepBody}>{step.description}</p>

        {showWalletPanel ? (
          <div className={css.gatePanel}>
            <Web3WalletAuthPanel />
          </div>
        ) : null}

        {showPersonaHint ? (
          <div className={css.gatePanel}>
            <p className={css.gateHint}>
              Activate the required persona from{' '}
              <Link to="/settings/profile">Profile & personas</Link> or connect a delegated wallet.
            </p>
          </div>
        ) : null}

        {step.fields && !showWalletPanel ? (
          <div className={css.fields}>
            {step.fields.map(field => (
              <label key={field.id} className={css.field}>
                <span className={css.fieldLabel}>{field.label}</span>
                {field.type === 'textarea' ? (
                  <textarea
                    className={css.input}
                    rows={3}
                    placeholder={field.placeholder}
                    value={formData[field.id] || ''}
                    onChange={e => handleFieldChange(field.id, e.target.value)}
                  />
                ) : field.type === 'select' ? (
                  <select
                    className={css.input}
                    value={formData[field.id] || ''}
                    onChange={e => handleFieldChange(field.id, e.target.value)}
                  >
                    <option value="">Select…</option>
                    {(field.options || []).map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className={css.input}
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    placeholder={field.placeholder}
                    value={formData[field.id] || ''}
                    onChange={e => handleFieldChange(field.id, e.target.value)}
                  />
                )}
              </label>
            ))}
          </div>
        ) : null}

        {(app.paymentOptions?.length || app.deliveryOptions?.length) && stepIndex >= 2 ? (
          <div className={css.crossApp}>
            {app.paymentOptions?.length ? (
              <div>
                <span className={css.crossLabel}>Payment options</span>
                <div className={css.chips}>
                  {app.paymentOptions.map(pid => (
                    <CrossAppChip key={pid} appId={pid} label={getAppLabel(pid)} />
                  ))}
                </div>
              </div>
            ) : null}
            {app.deliveryOptions?.length ? (
              <div>
                <span className={css.crossLabel}>Delivery options</span>
                <div className={css.chips}>
                  {app.deliveryOptions.map(pid => (
                    <CrossAppChip key={pid} appId={pid} label={getAppLabel(pid)} />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </article>

      <div className={css.actions}>
        {stepIndex > 0 ? (
          <SecondaryButtonInline type="button" onClick={handleBack}>
            Back
          </SecondaryButtonInline>
        ) : (
          <span />
        )}
        <div className={css.actionsRight}>
          <SecondaryButtonInline
            type="button"
            onClick={() => persistDraft(stepIndex, 'draft')}
            data-testid="guided-flow-save"
          >
            Save to Activity
          </SecondaryButtonInline>
          {isLastStep && canProceed ? (
            <PrimaryButton
              type="button"
              onClick={() => {
                persistDraft(stepIndex, 'ready');
                window.location.assign(resolveBxAppLaunchHref(app));
              }}
            >
              {app.launchLabel}
            </PrimaryButton>
          ) : (
            <PrimaryButton type="button" onClick={handleNext} disabled={!canProceed}>
              Continue
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidedAppFlow;
